/**
 * Book a Free Call — the self-hosted scheduler (replaces the Calendly embed).
 *
 * Two-column layout: a rail explaining how the call runs, and the BookingWidget
 * for a fast pick-a-time flow. The longer qualifying intake lives at
 * /get-started. UTM params on the URL are forwarded into the booking's
 * metadata so outreach attribution reaches the CRM.
 */
import { Clock, Video } from 'lucide-react'
import { BookingWidget } from '@/components/BookingWidget'
import { useSEO } from '@/hooks/useSEO'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'

function useUtmMetadata(): Record<string, string> {
  if (typeof window === 'undefined') return {}
  const params = new URLSearchParams(window.location.search)
  const meta: Record<string, string> = { source_page: '/book' }
  for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
    const value = params.get(key)
    if (value) meta[key] = value
  }
  return meta
}

const MOVEMENTS = [
  {
    numeral: 'I',
    title: 'Where things stand',
    body: 'We open your current site together and walk through what works and what gets in your way.',
  },
  {
    numeral: 'II',
    title: "What's possible",
    body: 'What a rebuild looks like for an organization like yours, with live examples from similar groups.',
  },
  {
    numeral: 'III',
    title: 'Your next steps',
    body: 'You leave with a short, concrete plan, whether or not you hire me.',
  },
]

function MovementList({ className }: { className?: string }) {
  return (
    <ol className={className}>
      {MOVEMENTS.map((m) => (
        <li key={m.numeral} className="flex gap-4">
          <span className="text-accent w-6 shrink-0 pt-0.5 text-right font-serif text-lg leading-none" aria-hidden>
            {m.numeral}.
          </span>
          <div>
            <div className="text-foreground font-medium">{m.title}</div>
            <p className="text-muted-foreground mt-1 text-sm leading-relaxed">{m.body}</p>
          </div>
        </li>
      ))}
    </ol>
  )
}

export default function BookPage() {
  const breadcrumbs = generateBreadcrumbs('/book', breadcrumbLabels)
  const metadata = useUtmMetadata()

  useSEO({
    title: 'Book a Free Call',
    description: 'Grab a free 30-minute call to talk through your organization and whether Fugue Lab is a fit.',
    url: '/book',
    canonical: '/book',
    breadcrumbs,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: 'Book a Free Call with Fugue Lab',
      description: "Schedule a free consultation to discuss your music organization's digital needs.",
      mainEntity: {
        '@type': 'Organization',
        name: 'Fugue Lab',
        email: 'hello@fuguelab.com',
        url: 'https://fuguelab.com',
      },
    },
  })

  return (
    <div className="bg-background min-h-screen py-16 sm:py-20">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-14">
        {/* Rail: what the call is and how it runs */}
        <div className="mx-auto w-full max-w-2xl lg:mx-0 lg:max-w-none">
          <p className="text-caption">Free strategy call</p>
          <h1 className="text-foreground mt-2 text-3xl font-bold tracking-tight md:text-4xl">Book a call</h1>
          <p className="text-muted-foreground mt-3 text-lg">
            Thirty minutes, one on one, looking at your site together.
          </p>

          <div className="text-muted-foreground mt-4 flex items-center gap-4 text-sm">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-4 w-4" aria-hidden /> 30 minutes
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Video className="h-4 w-4" aria-hidden /> Google Meet
            </span>
          </div>

          <h2 className="sr-only">How the call runs</h2>
          <MovementList className="border-border/60 mt-8 hidden space-y-6 border-t pt-8 lg:block" />
        </div>

        {/* Scheduler */}
        <div>
          <BookingWidget metadata={metadata} showHeader={false} analyticsSource="book-page" />

          {/* On smaller screens the rail collapses; the agenda follows the widget. */}
          <MovementList className="mx-auto mt-10 max-w-2xl space-y-6 lg:hidden" />
        </div>
      </div>
    </div>
  )
}
